import { useEffect, useRef } from 'react'
import { Transition } from 'react-transition-group'
import gsap from 'gsap'
import Link from 'next/link'

import Container from '@/components/base/Container'
import Grid from '@/components/base/Grid'
import Logo from '@/components/base/Logo'
import Typography from '@/components/base/Typography'
import Burger from '@/components/layout/Burger'
import Menu from '@/components/layout/Menu'
import { useLenis, useLenisStore } from '@/contexts/lenis'
import { useStore } from '@/contexts/store'
import { useTheme } from '@/contexts/theme/useTheme'
import { TRANSITION_LEAVE_DURATION } from '@/contexts/transition'
import { useLayoutStore } from '@/store/layout'
import { useRouterStore } from '@/store/router'

import { cn } from '@/lib/utils'

function Header() {
  const { strings } = useStore()
  const { lenis } = useLenis()
  const { theme } = useTheme()
  const menu = useRef<HTMLDivElement>(null)
  const [menuOpen, headerHide, headerBackground] = useLayoutStore((state) => [
    state.menuOpen,
    state.headerHide,
    state.headerBackground
  ])
  const setLayoutState = useLayoutStore((state) => state.setLayoutState)

  useEffect(() => {
    const unsubscribe = useRouterStore.subscribe(
      (state) => state.currentRoute,
      () =>
        gsap.delayedCall(TRANSITION_LEAVE_DURATION, () =>
          setLayoutState({ menuOpen: false })
        )
    )

    return () => {
      unsubscribe()
    }
  }, [setLayoutState])

  useEffect(() => {
    if (!lenis) return
    if (menuOpen) {
      lenis.stop()
    } else {
      lenis.start()
    }
  }, [menuOpen, lenis])

  const lastScrollRef = useRef<number | null>(null)

  useEffect(() => {
    const unsubscribe = useLenisStore.subscribe(
      (state) => [state.scroll, state.direction],
      ([scroll, direction]) => {
        const currentScroll = scroll ?? 0
        const activeScroll = currentScroll > 200

        // Aggiorna lo sfondo dell'header in base allo scroll
        setLayoutState({ headerBackground: activeScroll })

        if (activeScroll) {
          if (direction === 1) {
            // Scrolling verso il basso: applica un threshold di 20px
            if (lastScrollRef.current === null) {
              // Imposta il punto di riferimento se non è ancora definito
              lastScrollRef.current = currentScroll
            } else if (currentScroll - lastScrollRef.current >= 20) {
              setLayoutState({ headerHide: true })
              // Aggiorna il riferimento per futuri controlli
              lastScrollRef.current = currentScroll
            }
          } else {
            // Scrolling verso l'alto: mostra l'header solo se lo scroll verso l'alto supera i 20px
            if (lastScrollRef.current === null) {
              lastScrollRef.current = currentScroll
            } else if (lastScrollRef.current - currentScroll >= 20) {
              setLayoutState({ headerHide: false })
              lastScrollRef.current = currentScroll
            }
          }
        } else {
          // Se lo scroll non è "attivo", mostra l'header e resetta il riferimento
          setLayoutState({ headerHide: false })
          lastScrollRef.current = null
        }
      }
    )

    return () => {
      unsubscribe()
    }
  }, [setLayoutState])

  return (
    <header
      style={{ '--header-line': '1.8rem' } as React.CSSProperties}
      className={cn([
        'fixed left-0 right-0 top-0 z-header flex h-header-height items-center',
        'border-b border-transparent',
        'transition-all duration-500 ease-out-cubic',
        {
          'border-dark-200 bg-white': headerBackground,
          '-translate-y-full': headerHide,
          'text-white': theme === 'dark',
          'border-dark-200 text-dark-500': theme === 'light'
        }
      ])}
    >
      <Container>
        <Grid className="relative z-20 h-[var(--header-line)] [&_>_div]:h-[var(--header-line)]">
          <div
            className={cn([
              'col-span-2 flex items-center md:col-span-4',
              'transition-transform duration-2200 ease-out-expo',
              {
                'pointer-events-none -translate-y-full': headerHide
              }
            ])}
            data-header-span
          >
            <Link href="/" scroll={false}>
              <Logo theme={menuOpen || headerBackground ? 'light' : theme} />
            </Link>
          </div>
          <div
            className={cn([
              'col-span-4 hidden text-center leading-[var(--header-line)] lg:block',
              'transition-transform duration-2200 ease-out-expo',
              {
                'pointer-events-none -translate-y-full': headerHide
              }
            ])}
            data-header-span
          >
            <Typography
              variant="label"
              className={cn('transition-all duration-300 ease-out-cubic', {
                'text-dark-500': menuOpen || headerBackground
              })}
            >
              {strings?.header_label}
            </Typography>
          </div>
          <div
            className={cn([
              'col-span-2 flex items-center justify-end lg:col-span-4',
              'transition-transform duration-2200 ease-out-expo',
              {
                'pointer-events-none -translate-y-full': headerHide
              }
            ])}
            data-header-span
          >
            <Burger
              onClick={() => setLayoutState({ menuOpen: !menuOpen })}
              active={menuOpen}
              theme={menuOpen || headerBackground ? 'light' : theme}
            />
          </div>
        </Grid>

        <Transition
          nodeRef={menu}
          in={menuOpen}
          timeout={{
            enter: 0,
            exit: 800
          }}
          unmountOnExit
        >
          {(state) => <Menu ref={menu} state={state} menuOpen={menuOpen} />}
        </Transition>
      </Container>
    </header>
  )
}

export default Header
